import { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import cx from 'classnames'
import { Typography, Button, TextField, FormControlLabel, Checkbox } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { LoadingButton } from '@mui/lab'

import { ArrowIcon } from 'icons'
import { useAlertForm } from '../../AlertFormProvider'
import TimeWindow from '../../TimeWindow/TimeWindow'
import EmailRecipients from '../../EmailRecipients/EmailRecipients'
import styles from '../../ParameterAlertForm.module.scss'
import stepStyles from './Settings.module.scss'

// Keep this outside Settings so it is not rebuilt on every render.
const SubmitError = ({ submitError, submitErrorMessages = {}, translate }) => {
  if (!submitError) return null
  const messageKey = submitErrorMessages[submitError.res?.status] ?? 'submitErrors.unknown'
  return (
    <Alert
      severity='error'
      className={styles.error}
    >
      {translate(messageKey)}
    </Alert>
  )
}

// The backend expects these day keys in this order.
const DAYS = ['Lu', 'Ma', 'Mi', 'ju', 'vi', 'Sa', 'Do']

// The user finishes the alert settings and submits the form.
const Settings = () => {
  const {
    state: { activeTab, isSubmitting, submitError },
    actions,
    meta: { translate, offset, ALL_DAY_PERIOD, initialValues, submitLabel, submitErrorMessages }
  } = useAlertForm()

  const { register, errors, setError, clearErrors } = useForm()

  // --- Alarm name ---
  const [alarmName, setAlarmName] = useState(initialValues?.alarmName ?? '')
  const [showMissingNameError, setShowMissingNameError] = useState(false)

  const [emails, setEmails] = useState(initialValues?.emails ?? {})
  // Valid recipient emails reported by EmailRecipients. Used to ignore stale
  // selections (a deleted recipient still marked true in the map).
  const [availableRecipients, setAvailableRecipients] = useState([])

  const [typeCheckedBox, setTypeCheckedBox] = useState(initialValues?.occupancyDays ?? {})
  const [isCheckAllDay, setIsCheckAllDay] = useState(() =>
    DAYS.every(day => initialValues?.occupancyDays?.[day] === true)
  )

  // TimeWindow sends the current schedule here.
  const [timingData, setTimingData] = useState({
    isTimeRange: false,
    period1: [],
    period2: [],
    auxPeriod: [],
    auxPeriod2: [],
    auxDays: undefined,
    secondOccupationHoursChecked: false,
    isValid: true
  })
  // Keep this function unchanged between renders.
  const handleTimingChange = useCallback(data => {
    setTimingData(data)
  }, [])

  // Clear the email error when a valid address is selected. Selections not in
  // the current recipient list are ignored (stale entries).
  useEffect(() => {
    const hasValidSelection = Object.entries(emails).some(
      ([email, checked]) => checked && availableRecipients.includes(email)
    )
    if (hasValidSelection) clearErrors('emailRecipients')
  }, [emails, availableRecipients, clearErrors])

  const handleChangeAlarmName = event => {
    setAlarmName(event.target.value)
    if (event.target.value.trim() !== '') setShowMissingNameError(false)
  }

  const handleChangeCheckbox = event => {
    clearErrors('noChecked')
    setTypeCheckedBox({ ...typeCheckedBox, [event.target.value]: event.target.checked })
  }

  const handleSelectAllDays = () => {
    const allSelected = DAYS.reduce((acc, d) => ({ ...acc, [d]: true }), {})
    setIsCheckAllDay(prev => {
      const next = !prev
      setTypeCheckedBox(next ? allSelected : {})
      if (next) clearErrors('noChecked')
      return next
    })
  }

  // Check all fields before submitting.
  const onClickCreate = () => {
    let hasErrors = false

    if (!alarmName.trim()) {
      setShowMissingNameError(true)
      hasErrors = true
    } else {
      setShowMissingNameError(false)
    }

    // Keep selected emails even when the list is filtered, but ignore any
    // selection that is no longer a valid recipient (for example one that was
    // deleted after being checked).
    const selectedEmailRecipients = Object.entries(emails)
      .filter(([email, checked]) => checked && availableRecipients.includes(email))
      .map(([email]) => email)
    if (selectedEmailRecipients.length === 0) {
      setError('emailRecipients', { shouldFocus: 'emailRecipients' })
      hasErrors = true
    } else {
      clearErrors('emailRecipients')
    }

    if (!DAYS.some(d => typeCheckedBox[d])) {
      setError('noChecked', { shouldFocus: 'noChecked' })
      hasErrors = true
    } else {
      clearErrors('noChecked')
    }

    if (timingData.isTimeRange && !timingData.isValid) {
      hasErrors = true
    }

    if (hasErrors) return

    actions.submit({
      alarmName,
      emailRecipients: selectedEmailRecipients,
      occupancyDays: typeCheckedBox,
      // The backend uses ['00:00', '00:00'] for an all-day alert.
      period1: timingData.isTimeRange ? timingData.period1 : ALL_DAY_PERIOD,
      ...(timingData.isTimeRange && timingData.secondOccupationHoursChecked
        ? { period2: timingData.period2 }
        : {}),
      ...(timingData.isTimeRange && timingData.auxPeriod?.length > 0
        ? { auxPeriod: timingData.auxPeriod, auxDays: timingData.auxDays }
        : {}),
      ...(timingData.isTimeRange && timingData.auxPeriod2?.length > 0
        ? { auxPeriod2: timingData.auxPeriod2 }
        : {}),
      offset: offset.toString()
    })
  }

  const daysTranslated = [
    translate('addReportModal.monday'),
    translate('addReportModal.tuesday'),
    translate('addReportModal.wednesday'),
    translate('addReportModal.thursday'),
    translate('addReportModal.friday'),
    translate('addReportModal.saturday'),
    translate('addReportModal.sunday')
  ]

  return (
    <div style={{ display: activeTab === 3 ? 'block' : 'none' }}>
      <div className={styles.nav}>
        <Typography className={styles.subheaderParams}>
          {translate('addParameterAlertModal.navParams')}
        </Typography>
        <Typography className={styles.subheaderValues}>
          {translate('addParameterAlertModal.navValues')}
        </Typography>
        <Typography className={styles.subheaderDevices}>
          {translate('addParameterAlertModal.navDevices')}
        </Typography>
        <Typography className={styles.subheaderParamsSelected}>
          {translate('addParameterAlertModal.navEmails')}
        </Typography>
      </div>

      {/* Alarm name */}
      <TextField
        variant='outlined'
        required
        fullWidth
        id='alarmsName'
        onChange={handleChangeAlarmName}
        label={translate('alertsPage.alarmsName')}
        onKeyPress={e => {
          e.key === 'Enter' && e.preventDefault()
        }}
        inputRef={register()}
        name='alarmsName'
        autoComplete='alarmsName'
        value={alarmName}
        className={cx(styles.input, { [stepStyles.hasError]: showMissingNameError })}
      />
      {showMissingNameError && (
        <div className={styles.error}>
          <small>{translate('validation.alertNameRequired')}</small>
        </div>
      )}

      {/* Email recipients */}
      <EmailRecipients
        selectedEmails={emails}
        onSelectionChange={setEmails}
        onRecipientsChange={setAvailableRecipients}
        showError={!!errors.emailRecipients}
      />

      {/* Days of the week */}
      <div className={stepStyles.selectDays}>
        {translate('addParameterAlertModal.selectDaysToReceive')}
      </div>
      <FormControlLabel
        className={stepStyles.selectAllDays}
        control={
          <Checkbox
            className={cx(styles.checkbox)}
            classes={{ checked: styles.checkedBox }}
            name='selectAllDay'
            id='selectAllDay'
            onChange={handleSelectAllDays}
            checked={isCheckAllDay}
          />
        }
        label={translate('addAlertModal.selectAll')}
      />
      <table className={stepStyles.checkboxFormDivA}>
        <tbody>
          <tr>
            {daysTranslated.map((col, i) => (
              <td
                key={i + 'daysTranslated'}
                className={stepStyles.subInfo1}
              >
                {col}
              </td>
            ))}
          </tr>
          <tr>
            {DAYS.map((day, i) => (
              <td
                key={'day' + i}
                className={stepStyles.subInfo1}
              >
                <FormControlLabel
                  className={stepStyles.checkboxFormDays}
                  control={
                    <Checkbox
                      className={cx(stepStyles.checkboxAligned)}
                      classes={{ checked: styles.checkedBox }}
                      checked={typeCheckedBox[day] === true}
                      required
                      name='column'
                      value={day}
                      onChange={handleChangeCheckbox}
                      disableRipple
                    />
                  }
                />
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      {errors.noChecked && (
        <div className={styles.error}>
          <small>{translate('validation.anyField')}</small>
        </div>
      )}

      {/* Schedule */}
      <TimeWindow
        typeCheckedBox={typeCheckedBox}
        onTimingChange={handleTimingChange}
      />

      {/* Submit error */}
      <SubmitError
        submitError={submitError}
        submitErrorMessages={submitErrorMessages}
        translate={translate}
      />

      {/* Form actions */}
      {showMissingNameError && (
        <div className={styles.error}>
          <small>{translate('validation.completeRequiredFields')}</small>
        </div>
      )}
      <div className={styles.buttonWrapper}>
        <LoadingButton
          fullWidth
          variant='contained'
          className={stepStyles.buttonSiguiente3}
          loading={isSubmitting}
          disabled={isSubmitting}
          loadingPosition='end'
          onClick={onClickCreate}
        >
          {translate(submitLabel)}
        </LoadingButton>
      </div>
      <div className={styles.buttonWrapper}>
        <Button
          fullWidth
          variant='contained'
          className={styles.buttonCancelar}
          onClick={actions.goBack}
          disabled={isSubmitting}
        >
          <ArrowIcon
            className={styles.arrow}
            direction='left'
          />
          {translate('back')}
        </Button>
      </div>
    </div>
  )
}

export default Settings
